<template>
  <div class="am3-input-mobile">
    <div class="flex items-end relative [*_div]:radius-none [&_input]:bg-transparent  [&_input]:border-none">
      <div class="w-[120px] [&_input]:rounded-r-none [&_input]:outline-transparent [&_input]:outline-0 [&_input]:outline-offset-0 [&_input]:focus-within:border-brand [&_input]:focus-within:outline-brand">
        <AM3CountrySelect
          ref="countryInputRef"
          v-model="countryCode"
          :value-type="'code'"
          :display-type="'code'"
          :label="label"
          :placeholder="'Code'"
          :disabled="disabled"
          :data-test-id="generateDataTestId(dataTestId, 'mobile-input')"
          :show-selection-indicators="false"
          @select="handleCountrySelect"
        >
          <template
            v-if="required"
            #label
          >
            <span>*</span>
          </template>
        </AM3CountrySelect>
      </div>

      <AM3InputText
        ref="textInputRef"
        v-model="nationalNumber"
        :placeholder="'Mobile number'"
        :disabled="disabled"
        :restrict="restrictInput"
        :label="false"
        :data-test-id="dataTestId"
        class="flex-1 mt-[30px] [&_input]:rounded-l-none [&_.am3-input-wrapper]:border-l-0 [&_.am3-input-wrapper]:rounded-l-none [&_.am3-input-wrapper]:rounded-r-md"
        @update:model-value="handleNumberInput"
        @blur="handleBlur"
      />
    </div>
    <AM3InputError :error="validationState" />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import { parsePhoneNumber, ParseError } from 'libphonenumber-js/mobile'
import AM3CountrySelect from './AM3InputCountry.vue'
import AM3InputText from '@/components/am3-generic/inputs/entry/AM3InputText.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'

export default defineComponent({
  name: 'AM3InputMobile',

  components: {
    AM3InputText,
    AM3CountrySelect,
    AM3InputError,
  },

  props: {
    modelValue: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: 'Mobile number',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    multipleLines: {
      type: Boolean,
      default: false,
    },
    dataTestId: {
      type: String,
      default: 'mobile',
    },
    validate: {
      type: Function,
      default: null,
    },
    validateOnBlur: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const countryCode = ref('')
    const nationalNumber = ref('')
    const textInputRef = ref(null)
    const countryInputRef = ref(null)

    // Utility function to parse pasted phone number and extract country code and national number
    const parsePastedPhoneNumber = (pastedText: string) => {
      // Remove any non-digit and non-plus characters first
      const cleanedText = pastedText.replace(/[^\d+\-\s()]/g, '').trim()

      try {
        // Try to parse with libphonenumber-js
        const phoneNumber = parsePhoneNumber(cleanedText)
        if (phoneNumber && phoneNumber.isValid()) {
          return {
            countryCode: `+${phoneNumber.countryCallingCode}`,
            nationalNumber: phoneNumber.nationalNumber,
            success: true,
          }
        }
      }
      catch (error) {
        // If parsing fails, try to extract manually
      }

      // Manual extraction for common formats
      // Check if it starts with + and has country code
      if (cleanedText.startsWith('+')) {
        const digitsOnly = cleanedText.replace(/[^\d]/g, '')

        // Try common country codes (1-4 digits)
        for (let i = 1; i <= 4; i++) {
          const potentialCountryCode = '+' + digitsOnly.substring(0, i)
          const potentialNationalNumber = digitsOnly.substring(i)

          if (potentialNationalNumber.length >= 6) { // Minimum reasonable phone number length
            try {
              const testNumber = parsePhoneNumber(potentialCountryCode + potentialNationalNumber)
              if (testNumber && testNumber.isValid()) {
                return {
                  countryCode: potentialCountryCode,
                  nationalNumber: potentialNationalNumber,
                  success: true,
                }
              }
            }
            catch (error) {
              continue
            }
          }
        }
      }

      // If no country code, try with current country code if available
      if (countryCode.value) {
        const digitsOnly = cleanedText.replace(/[^\d]/g, '')
        try {
          const testNumber = parsePhoneNumber(countryCode.value + digitsOnly)
          if (testNumber && testNumber.isValid()) {
            return {
              countryCode: countryCode.value,
              nationalNumber: digitsOnly,
              success: true,
            }
          }
        }
        catch (error) {
          // Continue to fallback
        }
      }

      return {
        countryCode: '',
        nationalNumber: cleanedText.replace(/[^\d]/g, ''),
        success: false,
      }
    }

    const validateMobileNumber = (value: string) => {
      if (!props.required && !nationalNumber.value) return true
      if (props.required && !value) return 'This is a required field'
      if (!countryCode.value) return 'Please select a country code'

      try {
        const phoneNumber = parsePhoneNumber(value)

        if (!phoneNumber.isPossible()) {
          return 'Number length is invalid for selected country'
        }

        if (!phoneNumber.isValid()) {
          return 'Invalid mobile number format'
        }

        return true
      }
      catch (error) {
        if (error instanceof ParseError) {
          return 'Invalid phone number format'
        }
        return 'Invalid mobile number'
      }
    }

    // Use the user-provided validate function or fall back to our internal validation
    const validateFn = (value: string) => {
      const fullNumber = countryCode.value + nationalNumber.value
      return validateMobileNumber(fullNumber)
    }

    // Setup form validation using our composable
    const {
      validationState,
      runValidation: _runValidation,
      resetValidation,
    } = useFormValidation(validateFn, props.modelValue, props.required)

    const validateMobile = () => {
      // Always use the current full number
      const fullNumber = countryCode.value + nationalNumber.value
      const validationResult = validateFn(fullNumber)
      validationState.value = validationResult === true ? '' : (typeof validationResult === 'string' ? validationResult : 'Invalid number')
      return validationResult === true
    }

    // Public method to run validation - matches AM3InputText pattern
    const runValidation = () => {
      // Always use the current full number for validation
      const fullNumber = countryCode.value + nationalNumber.value
      const result = _runValidation(fullNumber, props.required)
      validationState.value = result === true ? '' : (typeof result === 'string' ? result : 'Invalid number')
      return result
    }

    watch(() => props.modelValue, (newValue) => {
      if (!newValue) {
        countryCode.value = ''
        nationalNumber.value = ''
        return
      }

      try {
        const phoneNumber = parsePhoneNumber(newValue)
        countryCode.value = `+${phoneNumber.countryCallingCode}`
        nationalNumber.value = phoneNumber.nationalNumber
      }
      catch (error) {
        if (error instanceof ParseError) {
          console.warn('Invalid phone number format')
        }
      }
    }, { immediate: true })

    const emitValue = () => {
      const value = countryCode.value + nationalNumber.value
      emit('update:modelValue', value)
    }

    const handleNumberInput = (value: string) => {
      nationalNumber.value = value
      emitValue()
      validateMobile() // Validate as the user types rather than waiting for blur
    }

    const handleBlur = () => {
      if (props.validateOnBlur) {
        validateMobile()
      }
    }

    const handleCountrySelect = (country: any) => {
      if (!country) {
        console.warn('No country selected')
        return
      }

      if (typeof country === 'string') {
        countryCode.value = country
      }
      else if (country && country.code) {
        countryCode.value = country.code
      }
      else {
        console.warn('Invalid country format', country)
        return
      }

      emitValue()
      validateMobile() // Keep validation on country select as it's not a blur event
    }

    const restrictInput = (event: Event) => {
      if (event instanceof KeyboardEvent) {
        // Allow Ctrl+A / Cmd+A for select all, Ctrl+C / Cmd+C for copy
        if (event.ctrlKey || event.metaKey) {
          return true
        }

        return /^\d$/.test(event.key)
          || ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key)
      }

      if (event instanceof ClipboardEvent && event.type === 'paste') {
        // Handle paste event
        event.preventDefault()

        const pastedData = event.clipboardData?.getData('text') || ''
        if (!pastedData) return false

        const parsedPhone = parsePastedPhoneNumber(pastedData)

        if (parsedPhone.success && parsedPhone.countryCode) {
          // Update both country code and national number
          countryCode.value = parsedPhone.countryCode
          nationalNumber.value = parsedPhone.nationalNumber
          emitValue()

          // Auto-validate the pasted number
          setTimeout(() => {
            validateMobile()
          }, 0)
        }
        else if (parsedPhone.nationalNumber) {
          // Only update national number if no valid country code found
          nationalNumber.value = parsedPhone.nationalNumber
          emitValue()
        }

        return false // Prevent default paste behavior
      }

      return true
    }

    return {
      countryCode,
      nationalNumber,
      validateMobileNumber,
      emitValue,
      generateDataTestId,
      handleNumberInput,
      handleCountrySelect,
      restrictInput,
      handleBlur,
      textInputRef,
      countryInputRef,
      validationState,
      runValidation,
      resetValidation,
    }
  },
})
</script>

<style>
.am3-input-mobile .am3-input-wrapper:nth-child(1) {
  border-left: 1px solid transparent;
}

.am3-input-mobile .am3-input-wrapper:nth-child(2) {
    border-right: 1px solid var(--surface-dark) ;
    border-radius: 6px 0 0 6px !important ;
    height: 44px;
}

.am3-input-mobile .am3-input-wrapper:nth-child(1):hover  {
  border-left: 1px solid var(--brand-color)  ;
}

.am3-input-mobile .am3-input-wrapper:nth-child(2):hover  {
    border-right: 1px solid var(--brand-color)  ;

}
</style>
